import completeProfileImage from "../images/athlete-home/complete-profile.png";
import ncaaTrackerImage from "../images/athlete-home/ncaa-tracker.png";
import collegeListImage from "../images/athlete-home/college-list.png";
import nilMicrocourseImage from "../images/athlete-home/nil-microcourse.png";
import mediaKitImage from "../images/athlete-home/media-kit.png";
import hubProgramImage from "../images/athlete-home/hub-program.png";
import calendarImage from "../images/athlete-home/calendar.png";

export type AthleteHomeActionAccent = "blue" | "green" | "slate";

export type AthleteHomeActionId =
    | "profile"
    | "ncaa"
    | "college-list"
    | "nil-course"
    | "media-kit"
    | "hub"
    | "calendar";

export type AthleteHomeAction = {
    id: AthleteHomeActionId;
    title: string;
    description: string;
    ctaLabel: string;
    destination: string;
    image: string;
    imageAlt: string;
    accent: AthleteHomeActionAccent;
};

const ATHLETE_HOME_ACTIONS: AthleteHomeAction[] = [
    {
        id: "profile",
        title: "Complete Your Profile",
        description:
            "Your profile is the foundation of your Polaris Athlete Hub experience. Add your information and help your support team better guide you throughout your journey.",
        ctaLabel: "Get started",
        destination: "/athlete/profile",
        image: completeProfileImage,
        imageAlt: "Preview of athlete profile setup",
        accent: "blue",
    },
    {
        id: "ncaa",
        title: "Start Your NCAA Eligibility Tracker",
        description:
            "Stay on top of your NCAA academic progress from day one. Your Eligibility Tracker helps you understand where you stand, identify any gaps in your core courses, and take action early to keep your college athletics opportunities on track.",
        ctaLabel: "Start Eligibility Tracker",
        destination: "/athlete/ncaa",
        image: ncaaTrackerImage,
        imageAlt: "Preview of NCAA eligibility tracker",
        accent: "green",
    },
    {
        id: "college-list",
        title: "Build Your List",
        description:
            "Every college journey starts somewhere. Explore schools, star your favorites, and begin creating a personalized list that will grow alongside your academic and athletic goals.",
        ctaLabel: "Explore Colleges",
        destination: "/athlete/college-planning",
        image: collegeListImage,
        imageAlt: "Preview of college list planning",
        accent: "slate",
    },
    {
        id: "nil-course",
        title: "Complete Your First NIL Microcourse",
        description:
            "Opportunities start with knowledge. Learn the fundamentals of NIL, personal branding, and responsible decision-making through microcourses. Then share your badge on LinkedIn.",
        ctaLabel: "Explore NIL Micro-courses",
        destination: "/athlete/nil-edu",
        image: nilMicrocourseImage,
        imageAlt: "Preview of NIL microcourse",
        accent: "blue",
    },
    {
        id: "media-kit",
        title: "Create Your NIL Media Kit",
        description:
            "Build your NIL Media Kit to showcase your profile and personal brand. This is the first step to connect with internships, brands, and opportunities.",
        ctaLabel: "Get started",
        destination: "/athlete/nil-edu?tab=media-kit",
        image: mediaKitImage,
        imageAlt: "Preview of NIL media kit",
        accent: "green",
    },
    {
        id: "hub",
        title: "Explore Your Organization's Hub",
        description:
            "Welcome to your school's Hub. Explore resources, announcements, videos, and important information shared by your counselors, coaches, and support staff.",
        ctaLabel: "Explore Hub",
        destination: "/athlete/program",
        image: hubProgramImage,
        imageAlt: "Preview of organization hub resources",
        accent: "slate",
    },
    {
        id: "calendar",
        title: "Explore Calendar of Events",
        description:
            "Stay organized by reviewing important recruiting, testing, academic, and application deadlines. Your Future-Ready Calendar helps you focus on what matters most and never miss an important milestone.",
        ctaLabel: "Open Calendar",
        destination: "/calendar",
        image: calendarImage,
        imageAlt: "Preview of calendar events",
        accent: "green",
    },
];

export function getAthleteHomeActions(): readonly AthleteHomeAction[] {
    return ATHLETE_HOME_ACTIONS;
}

export function getAthleteHomeActionById(id: string): AthleteHomeAction | undefined {
    return ATHLETE_HOME_ACTIONS.find((action) => action.id === id);
}

export function resolveAthleteHomePath(basePath: string, athleteId?: string | null): string {
    const trimmedId = athleteId?.trim();
    if (!trimmedId || trimmedId.includes("/") || trimmedId.includes("..")) {
        return basePath;
    }

    const [pathPart, queryPart] = basePath.split("?");
    const encodedId = encodeURIComponent(trimmedId);
    return queryPart ? `${pathPart}/${encodedId}?${queryPart}` : `${pathPart}/${encodedId}`;
}

export function isInternalAthleteHomeDestination(destination: string): boolean {
    if (!destination.startsWith("/")) {
        return false;
    }
    if (destination.startsWith("//") || destination.includes("://")) {
        return false;
    }
    return (
        destination.startsWith("/athlete/") ||
        destination.startsWith("/calendar") ||
        destination.startsWith("/drive")
    );
}
